import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@finitless/shared/components";
import { useToastContext } from "@finitless/shared/contexts";
import { useApiStore } from "@finitless/shared/stores";
import { useForgotPassword, useResetPassword } from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";
import { getBrandLogo } from "@finitless/shared/lib";
import finitlessLogo from "../../assets/finitless-logo.png";
import { useUiLocale } from "../../hooks/useUiLocale";
import { LocaleSelector } from "../ui";

const ForgotPasswordPage = () => {
  const { t } = useTranslation(["auth", "app", "toasts"]);
  const { locale, setLocale, supportedLocales } = useUiLocale();
  const brandLogo = getBrandLogo(finitlessLogo);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const random = searchParams.get("random");
  const projectFromParams = searchParams.get("project");

  const hasResetParams = !!(code || random);
  const isResetMode = !!(code && random);

  const [email, setEmail] = useState("");
  const [project, setProject] = useState(projectFromParams || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const forgotPasswordMutation = useForgotPassword();
  const resetPasswordMutation = useResetPassword();
  const setProjectConfig = useApiStore((state) => state.setProject);
  const toast = useToastContext();

  const handleLocaleChange = async (event) => {
    await setLocale(event.target.value);
  };

  const handleBackToLogin = () => {
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (hasResetParams && !isResetMode) {
      toast.error(t("validation.invalidResetLink", { ns: "auth" }));
      setTimeout(() => {
        handleBackToLogin();
      }, 2000);
    }
  }, [hasResetParams, isResetMode, toast, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isResetMode) {
      if (!password || !confirmPassword || !project) {
        toast.error(t("validation.fillAllFields", { ns: "auth" }));
        return;
      }

      if (password !== confirmPassword) {
        toast.error(t("validation.passwordsDoNotMatch", { ns: "auth" }));
        return;
      }

      if (password.length < 8) {
        toast.error(t("validation.passwordMinLength", { ns: "auth" }));
        return;
      }

      try {
        setProjectConfig(project);

        await resetPasswordMutation.mutateAsync({
          code,
          random,
          password,
          "confirm-password": confirmPassword,
        });

        toast.success(t("auth.resetPasswordSuccess", { ns: "toasts" }));
        setTimeout(() => {
          handleBackToLogin();
        }, 2000);
      } catch (error) {
        toast.error(
          error.message || t("auth.resetPasswordFailed", { ns: "toasts" }),
        );
      }
      return;
    }

    if (!email || !project) {
      toast.error(t("validation.fillAllFields", { ns: "auth" }));
      return;
    }

    try {
      setProjectConfig(project);
      const response = await forgotPasswordMutation.mutateAsync({ email });

      if (response.attemptsRemaining <= 2 && response.attemptsRemaining > 0) {
        toast.success(
          t("auth.forgotPasswordSentRemaining", {
            ns: "toasts",
            count: response.attemptsRemaining,
          }),
        );
      } else if (response.attemptsRemaining === 0) {
        toast.success(t("auth.forgotPasswordFinalAttempt", { ns: "toasts" }));
      } else {
        toast.success(t("auth.forgotPasswordSent", { ns: "toasts" }));
      }

      setTimeout(() => {
        handleBackToLogin();
      }, 2000);
    } catch (error) {
      toast.error(
        error.message || t("auth.forgotPasswordFailed", { ns: "toasts" }),
      );
    }
  };

  const isPending = isResetMode
    ? resetPasswordMutation.isPending
    : forgotPasswordMutation.isPending;

  const pageTitle = isResetMode
    ? t("resetPasswordTitle", { ns: "auth" })
    : t("forgotPasswordTitle", { ns: "auth" });
  const pageSubtitle = isResetMode
    ? t("resetPasswordSubtitle", { ns: "auth" })
    : t("forgotPasswordSubtitle", { ns: "auth" });

  return (
    <div className="forgot-password-page flex min-h-screen items-center justify-center bg-black px-4 py-4 sm:px-6 sm:py-8 lg:px-8">
      <div className="w-full max-w-md space-y-4 sm:space-y-6">
        <LocaleSelector
          id="forgot-locale-selector"
          label={t("language.label", { ns: "app" })}
          value={locale}
          onChange={handleLocaleChange}
          options={supportedLocales}
          containerClassName="w-full justify-end"
          labelClassName="text-slate-300"
          selectClassName="h-10 rounded-2xl border-white/20 bg-slate-950/90 text-sm"
        />

        <div className="text-center">
          <div className="mx-auto mb-4 flex items-center justify-center sm:mb-6">
            {brandLogo && (
              <img
                className="h-5 w-auto max-w-sm sm:h-8"
                src={brandLogo}
                alt="Finitless"
              />
            )}
          </div>
          <h2 className="text-2xl font-semibold text-white sm:text-3xl">
            {pageTitle}
          </h2>
          <p className="mt-2 text-sm text-gray-400">{pageSubtitle}</p>
        </div>

        <form className="mt-6 space-y-3" onSubmit={handleSubmit}>
          <div className="space-y-3">
            <Input
              id="project"
              name="project"
              type="text"
              autoComplete="off"
              required
              value={project}
              onChange={(event) => setProject(event.target.value)}
              placeholder={t("placeholders.project", { ns: "auth" })}
              disabled={isPending}
              label={t("fields.projectName", { ns: "auth" })}
              variant="dark"
              size="lg"
              labelClassName="text-gray-300"
            />

            {!isResetMode && (
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t("placeholders.email", { ns: "auth" })}
                disabled={isPending}
                label={t("fields.emailAddress", { ns: "auth" })}
                variant="dark"
                size="lg"
                labelClassName="text-gray-300"
              />
            )}

            {isResetMode && (
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={t("placeholders.newPassword", { ns: "auth" })}
                disabled={isPending}
                label={t("fields.newPassword", { ns: "auth" })}
                variant="dark"
                size="lg"
                labelClassName="text-gray-300"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isPending}
                    aria-label={
                      showPassword
                        ? t("accessibility.hidePassword", { ns: "auth" })
                        : t("accessibility.showPassword", { ns: "auth" })
                    }
                    className="text-gray-400 transition-colors hover:text-gray-300"
                  >
                    {showPassword ? (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                }
              />
            )}

            {isResetMode && (
              <Input
                id="confirm-password"
                name="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder={t("placeholders.confirmPassword", { ns: "auth" })}
                disabled={isPending}
                label={t("fields.confirmPassword", { ns: "auth" })}
                variant="dark"
                size="lg"
                labelClassName="text-gray-300"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isPending}
                    aria-label={
                      showConfirmPassword
                        ? t("accessibility.hidePassword", { ns: "auth" })
                        : t("accessibility.showPassword", { ns: "auth" })
                    }
                    className="text-gray-400 transition-colors hover:text-gray-300"
                  >
                    {showConfirmPassword ? (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                }
              />
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isPending}
              className="group relative flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-normal text-white transition-colors hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPending ? (
                <div className="flex items-center">
                  <svg
                    className="mr-3 -ml-1 h-5 w-5 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {isResetMode
                    ? t("resettingPassword", { ns: "auth" })
                    : t("sendingResetLink", { ns: "auth" })}
                </div>
              ) : isResetMode ? (
                t("resetPasswordButton", { ns: "auth" })
              ) : (
                t("sendResetLink", { ns: "auth" })
              )}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={handleBackToLogin}
              disabled={isPending}
              className="text-sm text-blue-500 transition-colors hover:text-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("backToSignIn", { ns: "auth" })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
